import React, { useState, useEffect } from 'react';
import { List, Datagrid, TextField, NumberField, ReferenceField, FunctionField, ExportButton } from 'react-admin';
import { get, cloneDeep } from 'lodash';
import { Button, Modal } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useDefaultFilter from '@/utils/useDefaultFilter';
import { Can, ACCESS, useAuth } from '@/utils/auth';
import { USER, TRANSACTION, MACHINE } from '@/vars';
import { Page } from '@/components';
import TransactionFilter from './TransactionFilter';
import CustomizedDialogs from './TransactionDetails';
import Toolbar from '@material-ui/core/Toolbar';
import {
  getProductDocs,
  getProductCategoryDocs,
  renderDateMY,
  renderTimeMY,
  getPaymentType,
  getProduct,
  getProductCategory,
  getFormattedSlotId,
  getStatus,
  getErrorCategoryMessage,
  renderTransactionStatus,
  setData,
  exportCSV,
} from '@/utils/transaction';
import {
  cashCoinTemplate,
  commercialGeneralTemplate,
  ipayTemplate,
  terminalTemplate,
  vircleTemplate,
  errorMessageTemplate,
  oneUtamaTemplate,
  eventParamsTemplate,
} from '@/vars/templates';

const TransactionList = props => {
  const { permissions } = useAuth();
  const isAdmin = permissions.role === USER.ROLE.ADMIN;
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const classes = useStyles();
  var filter = {
    ...useDefaultFilter('client'),
    mode: { operator: 'in', value: [MACHINE.MODE.COMMERCIAL, MACHINE.MODE.EVENT] },
  };
  if (!isAdmin) filter.status = TRANSACTION.STATUS.SUCCESS;

  const listProps = {
    ...props,
    filter,
    sort: { field: 'date', order: 'DESC' },
    bulkActionButtons: false,
    perPage: 25,
    filters: <TransactionFilter />,
  };

  const renderAdminMoreButtonField = record => {
    return (
      <Button
        size="medium"
        onClick={() => {
          console.log(isAdmin);
          setModalOpen(true);
          setModalContent(JSON.stringify(record, undefined, 3));
        }}
      >
        &gt;
      </Button>
    );
  };

  const renderCustomerMoreButtonField = record => {
    return <CustomizedDialogs {...record} className={classes} />;
  };

  const exporter = async posts => {
    const datasetCopy = cloneDeep(posts);

    if (isAdmin) {
      let datas = await Promise.all(
        //Promise.all resolve when all promises are resolved
        datasetCopy.map(async post => {
          post.time = renderTimeMY(post);
          post.date = renderDateMY(post);

          return post;
        })
      );
      exportCSV(datas, null);
      return;
    }

    const clientId = permissions.client_id;

    const products = await getProductDocs(clientId);
    const categories = await getProductCategoryDocs(clientId);

    let template = [...commercialGeneralTemplate];

    // helper so we don’t repeat `get(post,… ) != null`
    const hasEwallets = post => get(post, 'payment.gatewayID') != null || get(post, 'payment.ghlPaymentDetail') != null;

    if (posts.some(hasEwallets)) {
      // assign the _new_ concatenated array back
      template.push(...ipayTemplate);
    }

    if (posts.some(post => get(post, 'payment.cash') === true)) {
      template.push(...cashCoinTemplate);
    }

    if (posts.some(post => get(post, 'payment.terminal') === true)) {
      template.push(...terminalTemplate);
    }

    if (posts.some(post => get(post, 'payment.virclePaymentDetail'))) {
      template.push(...vircleTemplate);
    }

    if (posts.some(post => get(post, 'payment.onePay.payment'))) {
      template.push(...oneUtamaTemplate);
    }

    if (posts.some(post => get(post, 'payment.params'))) {
      template.push(...eventParamsTemplate);
    }

    template.push(...errorMessageTemplate);

    try {
      let datas = await Promise.all(
        datasetCopy.map(post => {
          post.time = renderTimeMY(post);
          post.date = renderDateMY(post);

          const { sku, serialNumber } = getProduct(post, products);
          setData(post, 'product.sku', sku);
          setData(post, 'product.serialNumber', serialNumber);

          const formattedSlotId = getFormattedSlotId(post);
          setData(post, 'product.formattedSlotId', formattedSlotId);

          const { category } = getProductCategory(post, categories);
          setData(post, 'product.categoryName', category);

          if (get(post, 'payment.gatewayID')) {
            setData(post, 'payment.paymentGateway', 'Ipay88');
            setData(post, 'payment.ewalletType', getPaymentType(post));
          } else {
            setData(post, 'payment.paymentGateway', getPaymentType(post));
          }
          setData(post, 'status', getStatus(post));

          const { category: errorCategory, message } = getErrorCategoryMessage(post);
          setData(post, 'error.message', message);

          return post;
        })
      );

      exportCSV(datas, template);
    } catch (error) {
      alert(error);
    }
  };

  const PostActions = ({
    basePath,
    currentSort,
    displayedFilters,
    filters,
    filterValues,
    resource,
    showFilter,
    total,
  }) => {
    filter.status = filterValues.status;

    return (
      <Toolbar>
        {filter &&
          React.cloneElement(filters, {
            resource,
            showFilter,
            displayedFilters,
            filterValues,
            context: 'button',
          })}
        <ExportButton
          disabled={total === 0}
          maxResults={total}
          resource={resource}
          sort={currentSort}
          filter={{ ...filterValues, ...filter }}
          exporter={exporter}
        />
      </Toolbar>
    );
  };

  return (
    <Can module={ACCESS.REPORT}>
      <Page sectionTitle="Sales Report" title="List transactions">
        <div
          style={{
            display: 'flex',
            flex: '1 1 auto',
            overflowY: 'hidden',
            overflowX: 'scroll',
          }}
        >
          <List {...listProps} actions={<PostActions />}>
            <Datagrid>
              <TextField label="Machine UUID" source="machine.machineUUID" />
              <FunctionField label="Date" render={renderDateMY} />
              <FunctionField label="Time" render={renderTimeMY} />
              {isAdmin && (
                <ReferenceField label="Company" source="client" reference="clients" link={false}>
                  <TextField source="companyName" />
                </ReferenceField>
              )}

              <TextField label="Product Name" source="product.name" />
              <FunctionField label="Product Slot" render={getFormattedSlotId} />
              <NumberField
                source="amount"
                label="Product Price (RM)"
                locales="en-MY"
                options={{ style: 'currency', currency: 'MYR' }}
                textAlign="left"
              />
              <FunctionField label="Gateway" render={getPaymentType} />
              <FunctionField label="Status" render={renderTransactionStatus} />
              {isAdmin ? (
                <FunctionField label="" render={renderAdminMoreButtonField} />
              ) : (
                <FunctionField label="" render={renderCustomerMoreButtonField} />
              )}
            </Datagrid>
          </List>
        </div>
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <div className={classes.paper}>
            <pre>{modalContent}</pre>
          </div>
        </Modal>
      </Page>
    </Can>
  );
};

const useStyles = makeStyles(theme => ({
  paper: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 500,
    maxHeight: '90%',
    overflow: 'scroll',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
}));

export default TransactionList;
